/**
 * Traduz a falha de rede/HTTP em uma frase que o cliente consegue agir.
 *
 * O backend tem mensagens boas para os casos previstos ("Senha incorreta",
 * "Código expirado"), mas nem tudo que chega em `data.message` foi escrito para
 * o usuário final: erro de integração vira `"SMS request failed: 400 {...}"`,
 * validação de schema vira um objeto, e 500 vira `"Internal server error"`.
 * Exibir isso é pior do que não exibir nada — o cliente não entende e não
 * descobre o que fazer.
 *
 * Por isso a mensagem do backend só passa quando se parece com português
 * escrito para pessoa; caso contrário cai no texto por status.
 */

const FALLBACK = 'Não foi possível concluir. Tente novamente.'

const POR_STATUS: Record<number, string> = {
  400: 'Não foi possível concluir com os dados informados. Confira e tente novamente.',
  401: 'Senha incorreta.',
  403: 'Você não tem permissão para fazer esta alteração.',
  404: 'Solicitação não encontrada. Comece de novo.',
  408: 'A conexão demorou demais. Tente novamente.',
  409: 'Este número já pertence a uma conta. Procure o atendimento.',
  429: 'Muitas tentativas. Aguarde alguns minutos e tente de novo.',
}

const ERRO_DE_SERVIDOR =
  'Nosso sistema está instável no momento. Tente novamente em alguns minutos.'

const SEM_RESPOSTA =
  'Não conseguimos falar com o servidor. Verifique sua conexão e tente novamente.'

/**
 * Marcas de texto que veio de máquina, não de redator: JSON embutido, termos em
 * inglês de stack e código de status no meio da frase.
 */
const MARCAS_TECNICAS =
  /[{}[\]]|https?:\/\/|\b(error|failed|exception|invalid|unauthorized|forbidden|internal server|request failed|timeout|undefined|null)\b|\b[45]\d{2}\b/i

/**
 * Frases-padrão do NestJS quando ninguém escreveu uma mensagem: passam pelo
 * teste de marcas técnicas (são só duas palavras em inglês) mas não dizem nada
 * ao cliente. O texto por status é sempre melhor que qualquer uma delas.
 */
const FRASES_PADRAO_HTTP = new Set([
  'bad request',
  'conflict',
  'forbidden',
  'gateway timeout',
  'not found',
  'payload too large',
  'request timeout',
  'service unavailable',
  'too many requests',
  'unauthorized',
  'unprocessable entity',
])

function ehMensagemParaUsuario(valor: unknown): valor is string {
  if (typeof valor !== 'string') return false

  const texto = valor.trim()

  if (texto.length === 0 || texto.length > 160) return false
  if (FRASES_PADRAO_HTTP.has(texto.toLowerCase())) return false

  return !MARCAS_TECNICAS.test(texto)
}

export function mensagemDeErro(error: any): string {
  const status: unknown = error?.response?.status
  const doBackend: unknown = error?.response?.data?.message

  if (ehMensagemParaUsuario(doBackend)) {
    return doBackend
  }

  if (typeof status !== 'number') {
    return SEM_RESPOSTA
  }

  if (status >= 500) {
    return ERRO_DE_SERVIDOR
  }

  return POR_STATUS[status] ?? FALLBACK
}
